import Link from "next/link";
import type { DefaultValues } from "react-hook-form";
import type { OfferContactFormDataT } from "@/app/schemas/zod/contact-form-schema";
import type {
  ContactFormConfigT,
  ContactFormFieldConfigT,
} from "./contact-form-types";

export type OfferContactFormFieldNameT =
  | "company"
  | "salutation"
  | "firstname"
  | "lastname"
  | "email"
  | "phone"
  | "comments"
  | "location"
  | "desiredFinancing"
  | "tradeIn"
  | "make"
  | "makeName"
  | "aktion"
  | "vehicleCondition"
  | "vehicleLicensePlate"
  | "vehicleVin"
  | "vehicleMileage"
  | "vehicleModel"
  | "privacy"
  | "mail"
  | "case"
  | "URL"
  | "source"
  | "dealerdesk";

export const offerContactFormDefaultValues: DefaultValues<OfferContactFormDataT> =
  {
    company: undefined,
    salutation: undefined,
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    comments: undefined,
    location: undefined,
    desiredFinancing: undefined,
    tradeIn: false,
    make: undefined,
    makeName: undefined,
    aktion: undefined,
    vehicleCondition: undefined,
    vehicleLicensePlate: undefined,
    vehicleVin: undefined,
    vehicleMileage: undefined,
    vehicleModel: undefined,
    privacy: false,
    mail: "",
    case: "OPPORTUNITY",
    URL: undefined,
    source: "Angebots Anfrage",
    dealerdesk: "Verkauf Darmstadt",
  };

export const vehicleContactFormDefaultValues: DefaultValues<OfferContactFormDataT> =
  {
    ...offerContactFormDefaultValues,
    case: "OPPORTUNITY",
  };

const primaryFields: ContactFormFieldConfigT<OfferContactFormFieldNameT>[] = [
  {
    name: "company",
    type: "text",
    label: "Firma",
    placeholder: "Firma",
  },
  {
    name: "salutation",
    type: "select",
    label: "Anrede",
    placeholder: "Bitte auswählen...",

    options: [
      { value: "Herr", label: "Herr" },
      { value: "Frau", label: "Frau" },
      { value: "Divers", label: "Divers" },
    ],
  },
  {
    name: "firstname",
    type: "name",
    label: "Vorname",
    placeholder: "Vorname",
  },
  {
    name: "lastname",
    type: "name",
    label: "Name",
    placeholder: "Nachname",
  },
  {
    name: "email",
    type: "text",
    inputType: "email",
    label: "E-Mail",
    placeholder: "E-Mail",
  },
  {
    name: "phone",
    type: "phone",
    label: "Telefon",
    placeholder: "+49 (123) 45678902",
  },
  {
    name: "location",
    type: "select",
    label: "Wunschstandort",
    placeholder: "Standort auswählen...",
    colSpan: 2,
  },
  {
    name: "desiredFinancing",
    type: "select",
    label: "Gewünschte Finanzierung",
    placeholder: "Bitte auswählen...",
    colSpan: 2,
    options: [
      { value: "barkauf", label: "Barkauf" },
      { value: "finanzierung", label: "Finanzierung" },
      { value: "leasing", label: "Leasing" },
    ],
  },
];

const vehicleFields: ContactFormFieldConfigT<OfferContactFormFieldNameT>[] = [
  {
    name: "makeName",
    type: "select",
    label: "Marke",
    placeholder: "Marke auswählen...",
  },
  {
    name: "vehicleLicensePlate",
    type: "text",
    label: "Kennzeichen",
    placeholder: "Kennzeichen",
  },
  {
    name: "vehicleVin",
    type: "text",
    label: "FIN",
    placeholder: "FIN",
  },
  {
    name: "vehicleMileage",
    type: "mileage",
    label: "Kilometerstand",
    placeholder: "Kilometerstand",
    unit: "km",
  },
  {
    name: "vehicleModel",
    type: "text",
    label: "Modell",
    placeholder: "Modellbeschreibung",
  },
];

const commentsField: ContactFormFieldConfigT<OfferContactFormFieldNameT> = {
  name: "comments",
  type: "textarea",
  label: "Gibt es weitere Bemerkungen oder Hinweise?",
  placeholder: "Ihre Nachricht...",
  colSpan: 2,
};

const tradeInField: ContactFormFieldConfigT<OfferContactFormFieldNameT> = {
  name: "tradeIn",
  type: "checkbox",
  colSpan: 2,
  label: "Ich möchte mein Fahrzeug in Zahlung geben",
};

const privacyField: ContactFormFieldConfigT<OfferContactFormFieldNameT> = {
  name: "privacy",
  type: "checkbox",
  colSpan: 2,
  label: (
    <span className="text-sm leading-relaxed whitespace-normal sm:whitespace-nowrap">
      Mit absenden bestätigen Sie unsere{" "}
      <Link href="/datenschutz" className="underline">
        Datenschutzerklärung
      </Link>
      .
    </span>
  ),
};

const honeypotField: ContactFormFieldConfigT<OfferContactFormFieldNameT> = {
  name: "mail",
  type: "honeypot",
};

const caseField: ContactFormFieldConfigT<OfferContactFormFieldNameT> = {
  name: "case",
  type: "select",
  hidden: true,
  options: [
    { value: "CASE", label: "CASE" },
    { value: "OPPORTUNITY", label: "OPPORTUNITY" },
  ],
};

const urlField: ContactFormFieldConfigT<OfferContactFormFieldNameT> = {
  name: "URL",
  type: "text",
  hidden: true,
};

const makeField: ContactFormFieldConfigT<OfferContactFormFieldNameT> = {
  name: "make",
  type: "text",
  hidden: true,
};

const aktionField: ContactFormFieldConfigT<OfferContactFormFieldNameT> = {
  name: "aktion",
  type: "text",
  hidden: true,
};

const vehicleConditionField: ContactFormFieldConfigT<OfferContactFormFieldNameT> =
  {
    name: "vehicleCondition",
    type: "text",
    hidden: true,
  };

// Configuration for used cars (Gebrauchtwagen) - location field is hidden
const primaryFieldsWithoutLocation: ContactFormFieldConfigT<OfferContactFormFieldNameT>[] =
  [...primaryFields.filter((field) => field.name !== "location")];

export const vehicleContactFormConfig: ContactFormConfigT<OfferContactFormFieldNameT> =
  {
    sections: [
      {
        id: "primary",
        layout: "grid",
        columns: 2,
        fields: [
          ...primaryFieldsWithoutLocation,
          honeypotField,
          caseField,
          urlField,
          makeField,
          aktionField,
          vehicleConditionField,
        ],
      },
      {
        id: "tradeIn",
        layout: "grid",
        columns: 2,
        fields: [tradeInField],
      },
      {
        id: "vehicle",
        title: "Fahrzeugdaten (optional)",
        collapsible: true,
        defaultExpanded: true,
        layout: "grid",
        columns: 2,
        fields: vehicleFields,
        conditionalRender: {
          fieldName: "tradeIn",
          condition: (value) => value === true,
        },
      },
      {
        id: "additional",
        layout: "grid",
        columns: 2,
        fields: [commentsField, privacyField],
      },
    ],
    honeypotFieldName: "mail",
    submitButtonLabel: "Nachricht senden",
    submittingLabel: "Wird gesendet...",
    successToast: {
      title: "Nachricht erfolgreich gesendet",
      description: "Wir werden uns in Kürze bei Ihnen melden.",
    },
    errorToast: {
      title: "Fehler beim Senden",
      description:
        "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.",
    },
  };

export const offerContactFormConfig: ContactFormConfigT<OfferContactFormFieldNameT> =
  {
    sections: [
      {
        id: "primary",
        layout: "grid",
        columns: 2,
        fields: [
          ...primaryFields,
          honeypotField,
          caseField,
          urlField,
          makeField,
          aktionField,
          vehicleConditionField,
        ],
      },
      {
        id: "tradeIn",
        layout: "grid",
        columns: 2,
        fields: [tradeInField],
      },
      {
        id: "vehicle",
        title: "Fahrzeugdaten (optional)",
        collapsible: true,
        defaultExpanded: true,
        layout: "grid",
        columns: 2,
        fields: vehicleFields,
        conditionalRender: {
          fieldName: "tradeIn",
          condition: (value) => value === true,
        },
      },
      {
        id: "additional",
        layout: "grid",
        columns: 2,
        fields: [commentsField, privacyField],
      },
    ],
    honeypotFieldName: "mail",
    submitButtonLabel: "Nachricht senden",
    submittingLabel: "Wird gesendet...",
    successToast: {
      title: "Nachricht erfolgreich gesendet",
      description: "Wir werden uns in Kürze bei Ihnen melden.",
    },
    errorToast: {
      title: "Fehler beim Senden",
      description:
        "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.",
    },
  };
